import { useCallback, useContext, useMemo, useState } from "react";
import {
    FinancialDocumentExtended,
    ImporterErrors,
} from "@common/types/financialDocument";
import { DISMISSABLE_IMPORTER_ERRORS } from "@common/helpers/financialDocument";
import { useRemoveImporterErrorMutation } from "@api/financialDocument.api";
import { CheckIcon, TriangleSmallIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useSettings } from "@components/settings/SettingsProvider";
import {
    EntitySettingsPages,
    WorkspaceSettingsPages,
} from "@components/settings/constants";
import { getEntityPaneKey } from "@components/settings/lib";
import { Loader } from "@design-system/Loader/Loader";
import {
    getDisplayedErrors,
    getDisplayErrorMessage,
    getInformationalImporterErrors,
    ImporterDisplayError,
    ImporterDisplayErrorKind,
    InvalidTransactionsSummary,
    shouldOfferFlipSignsInPreview,
} from "./helpers";
import { ImporterContext } from "./importer.context";
import { ImporterUpgradePlanButton } from "./ImporterUpgradePlanButton";
import styles from "./ImporterPage.module.scss";

interface Props {
    financialDocument: FinancialDocumentExtended;
    onFlipSigns?: () => void;
    onClose: () => void;
    isExtracting?: boolean;
    discrepancyAmount?: number;
    invalidTransactionsSummary?: InvalidTransactionsSummary;
}

export const ImporterPreviewCommandBar: React.FC<Props> = ({
    financialDocument,
    onFlipSigns,
    onClose,
    isExtracting,
    discrepancyAmount,
    invalidTransactionsSummary,
}) => {
    const { open: openSettings } = useSettings();
    const { readyToImportLabel, reconciliedFinancialAccount } =
        useContext(ImporterContext);

    const displayedErrors = useMemo(
        () =>
            getDisplayedErrors(financialDocument, reconciliedFinancialAccount),
        [financialDocument, reconciliedFinancialAccount],
    );

    const informationalErrors = useMemo(
        () => getInformationalImporterErrors(financialDocument),
        [financialDocument],
    );

    const [currentErrorIndex, setCurrentErrorIndex] = useState(0);

    const safeIndex = Math.min(
        currentErrorIndex,
        Math.max(displayedErrors.length - 1, 0),
    );
    const currentError: ImporterDisplayError | undefined =
        displayedErrors[safeIndex];

    const removeImporterErrorMutation =
        useRemoveImporterErrorMutation(financialDocument);

    const goToPreviousError = useCallback(() => {
        setCurrentErrorIndex((prev) => Math.max(0, prev - 1));
    }, []);

    const goToNextError = useCallback(() => {
        setCurrentErrorIndex((prev) =>
            Math.min(displayedErrors.length - 1, prev + 1),
        );
    }, [displayedErrors.length]);

    const openBookkeepingSettings = useCallback(() => {
        if (!financialDocument.entityId) {
            return;
        }
        const entityPaneKey = getEntityPaneKey(
            EntitySettingsPages.SETTINGS,
            financialDocument.entityId,
        );
        const path = financialDocument.financialAccountId
            ? [entityPaneKey, String(financialDocument.financialAccountId)]
            : [entityPaneKey];
        openSettings(path);
    }, [
        financialDocument.entityId,
        financialDocument.financialAccountId,
        openSettings,
    ]);

    const dismiss = useCallback(() => {
        if (
            currentError?.kind === ImporterDisplayErrorKind.IMPORTER_EXTRACTION
        ) {
            removeImporterErrorMutation.mutate(currentError.error);
        }
    }, [currentError, removeImporterErrorMutation]);

    if (isExtracting) {
        return (
            <CommandIsland className={styles.previewCommandBar}>
                <div className={styles.commandBarStatusMessage}>
                    <Loader inline />
                    <span>Extracting...</span>
                </div>
                <div className={styles.commandBarActions}>
                    <Button variant="primary" size="md" onClick={onClose}>
                        Back
                    </Button>
                </div>
            </CommandIsland>
        );
    }

    const hasErrors = displayedErrors.length > 0 && currentError;

    if (hasErrors) {
        const errorMessage = getDisplayErrorMessage(
            currentError,
            financialDocument,
            { discrepancyAmount, invalidTransactionsSummary },
        );
        const isImporterError =
            currentError.kind === ImporterDisplayErrorKind.IMPORTER_EXTRACTION;
        const importerError = isImporterError ? currentError.error : undefined;

        const isDismissable =
            importerError !== undefined &&
            DISMISSABLE_IMPORTER_ERRORS.includes(importerError);
        const showFlipSigns = shouldOfferFlipSignsInPreview(financialDocument, {
            onFlipSigns,
            activeImporterError: importerError,
        });
        const isPeriodLocked =
            importerError === ImporterErrors.PERIOD_IS_LOCKED;
        const isBeforeBookkeepingStart =
            importerError ===
            ImporterErrors.TRANSACTIONS_BEFORE_BOOKKEEPING_START;
        const isAfterBookkeepingEnd =
            importerError === ImporterErrors.TRANSACTIONS_AFTER_BOOKKEEPING_END;
        const isAfterAccountBookkeepingEnd =
            importerError ===
            ImporterErrors.TRANSACTIONS_AFTER_ACCOUNT_BOOKKEEPING_END;

        return (
            <CommandIsland className={styles.previewCommandBar}>
                <div className={styles.commandBarNav}>
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        aria-label="Previous error"
                        className={styles.commandBarNavLeft}
                        onClick={goToPreviousError}
                        disabled={safeIndex === 0}
                    >
                        <TriangleSmallIcon className="icon-rotate-90" />
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        aria-label="Next error"
                        className={styles.commandBarNavRight}
                        onClick={goToNextError}
                        disabled={safeIndex === displayedErrors.length - 1}
                    >
                        <TriangleSmallIcon className="icon-rotate-270" />
                    </Button>
                </div>
                <div className={styles.commandBarMessage}>
                    <span>
                        Review task {safeIndex + 1} of {displayedErrors.length}
                    </span>
                    <span className={styles.commandBarDot}>&middot;</span>
                    <span>{errorMessage}</span>
                </div>
                <div className={styles.commandBarActions}>
                    <Button variant="primary" size="md" onClick={onClose}>
                        Back
                    </Button>
                    {showFlipSigns && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onFlipSigns}
                        >
                            Flip signs
                        </Button>
                    )}
                    {isPeriodLocked && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() =>
                                openSettings([WorkspaceSettingsPages.LOCKS])
                            }
                        >
                            Go to Locks
                        </Button>
                    )}
                    {isBeforeBookkeepingStart && financialDocument.entityId && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={openBookkeepingSettings}
                        >
                            Update bookkeeping start date
                        </Button>
                    )}
                    {isAfterBookkeepingEnd && <ImporterUpgradePlanButton />}
                    {isAfterAccountBookkeepingEnd &&
                        financialDocument.entityId && (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={openBookkeepingSettings}
                            >
                                Update account end date
                            </Button>
                        )}
                    {isDismissable && (
                        <Button variant="primary" size="md" onClick={dismiss}>
                            Dismiss
                        </Button>
                    )}
                    {!isDismissable && !isPeriodLocked && (
                        <Button variant="primary" size="md" disabled>
                            Continue
                        </Button>
                    )}
                </div>
            </CommandIsland>
        );
    }

    const showFlipSigns = shouldOfferFlipSignsInPreview(financialDocument, {
        onFlipSigns,
    });

    const informationalMessage =
        informationalErrors[0] &&
        getDisplayErrorMessage(informationalErrors[0], financialDocument);

    return (
        <CommandIsland className={styles.previewCommandBar}>
            <div className={styles.commandBarReadyMessage}>
                <CheckIcon className="icon-color-gray-900" />
                <span>{readyToImportLabel}</span>
                {informationalMessage ? (
                    <>
                        <span className={styles.commandBarDot}>&middot;</span>
                        <span className={styles.commandBarInformationalText}>
                            {informationalMessage}
                        </span>
                    </>
                ) : null}
            </div>
            <div className={styles.commandBarActions}>
                {showFlipSigns && (
                    <Button variant="primary" size="md" onClick={onFlipSigns}>
                        Flip signs
                    </Button>
                )}
                <Button variant="primary" size="md" onClick={onClose}>
                    Finish review
                </Button>
            </div>
        </CommandIsland>
    );
};
